import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@amore/ui/components/dropdown-menu'
import {
  SortableDataList,
  SortableDataListHandle,
  SortableDataListItem,
} from '@amore/ui/components/sortable-data-list'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  ArrowRight,
  ChevronDown,
  Clock3,
  MapPin,
  PencilLine,
  Plus,
  Route,
  Users,
} from 'lucide-react'
import { useRef } from 'react'
import { plural } from '@/lib/format'
import { usePrototype } from '@/mock/store'
import type { CeremonyStep, WeddingData } from '@/mock/types'
import { DetailSheet } from '@/patterns/dialog'
import { EmptyState } from '@/patterns/empty-state'
import { ScreenSection } from '@/patterns/screen'
import { ceremonySummary } from './model'
import { CeremonyStepSheet } from './step-sheet'

const suggestions = [
  'Usazení hostů',
  'Nástup k obřadu',
  'Úvodní slovo',
  'Svatební sliby',
  'Výměna prstýnků',
  'Odchod a špalír',
  'Gratulace',
]

function moveStep(steps: CeremonyStep[], fromId: string, toId: string) {
  const from = steps.findIndex((step) => step.id === fromId)
  const to = steps.findIndex((step) => step.id === toId)
  const step = steps[from]
  if (!step || to < 0) return steps
  const next = steps.filter((item) => item.id !== fromId)
  next.splice(to, 0, step)
  return next
}

function stepPeople(step: CeremonyStep, data: WeddingData) {
  return step.people.flatMap((person) => {
    if (person.type === 'guest') {
      const guest = data.parties
        .flatMap((party) => party.persons)
        .find((guest) => guest.id === person.id)
      return guest ? [`${guest.firstName} ${guest.lastName}`.trim()] : []
    }
    const record = (person.type === 'supplier' ? data.suppliers : data.members).find(
      (record) => record.id === person.id,
    )
    return record ? [record.name] : []
  })
}

export function CeremonyScenarioPage() {
  const { data, canEdit, update } = usePrototype()
  const { event, venue } = ceremonySummary(data)
  const { steps } = data.ceremony
  const addButton = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const { stepId } = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const initialTitle = 'title' in search && typeof search.title === 'string' ? search.title : ''
  const step = steps.find((item) => item.id === stepId)
  const isNew = stepId === 'new'
  const returnFocus = useRef<string | null>(null)
  const openStep = (step: CeremonyStep) => {
    returnFocus.current = step.id
    void navigate({ to: `/ceremony/scenario/${step.id}` })
  }
  const closeStep = () => {
    const id = returnFocus.current ?? step?.id
    void navigate({ to: '/ceremony/scenario', search: {} })
    requestAnimationFrame(() => {
      const target =
        (id ? document.getElementById(`ceremony-step-${id}`) : null) ?? addButton.current
      target?.focus({ preventScroll: true })
    })
  }
  const setSteps = (recipe: (steps: CeremonyStep[]) => CeremonyStep[]) =>
    update((current) => ({
      ...current,
      ceremony: { ...current.ceremony, steps: recipe(current.ceremony.steps) },
    }))

  const add = (title: string) => {
    returnFocus.current = null
    void navigate({ to: '/ceremony/scenario/new', search: title ? { title } : {} })
  }

  return (
    <>
      <Card className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 items-start gap-3">
          <MapPin className="mt-0.5 size-4 shrink-0 text-action" aria-hidden="true" />
          <div className="min-w-0">
            <p className="break-words text-sm font-semibold">
              {event?.location || venue?.name || 'Místo zatím není vybrané'}
            </p>
            {event?.location && venue ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{venue.name}</p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <p className="inline-flex items-center gap-2 text-sm tabular-nums">
            <Clock3 className="size-4 shrink-0 text-action" aria-hidden="true" />
            {event?.time ? (
              <>
                <span className="sr-only">Začátek </span>
                {event.time}
              </>
            ) : (
              'Začátek zatím neurčený'
            )}
            {event?.endTime ? (
              <span className="text-muted-foreground">· konec {event.endTime}</span>
            ) : null}
          </p>
          <Link
            to="/ceremony"
            className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
          >
            Přehled obřadu <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        </div>
      </Card>
      <ScreenSection
        title="Scénář obřadu"
        description={
          canEdit
            ? 'Kliknutím na bod otevřete jeho detail. Pořadí změníte přetažením.'
            : 'Jednotlivé okamžiky vašeho obřadu tak, jak půjdou za sebou.'
        }
        action={
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button ref={addButton}>
                  <Plus className="size-4" aria-hidden="true" />
                  Přidat bod
                  <ChevronDown className="size-3.5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Co k vašemu obřadu patří?</DropdownMenuLabel>
                {suggestions.map((title) => (
                  <DropdownMenuItem key={title} onSelect={() => add(title)}>
                    {title}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => add('')}>
                  <Plus className="size-4" aria-hidden="true" />
                  Vlastní bod
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      >
        {steps.length ? (
          <Card padding="none" className="overflow-hidden">
            <SortableDataList
              aria-label="Body scénáře obřadu"
              appearance="divided"
              items={steps.map((step) => step.id)}
              onReorder={(from, to) => {
                if (canEdit && typeof from === 'string' && typeof to === 'string')
                  setSteps((steps) => moveStep(steps, from, to))
              }}
              accessibility={{
                screenReaderInstructions: {
                  draggable: 'Mezerníkem bod zvednete, šipkami přesunete a mezerníkem položíte.',
                },
              }}
            >
              {steps.map((step, index) => (
                <ScenarioRow
                  key={step.id}
                  step={step}
                  index={index}
                  canEdit={canEdit}
                  people={stepPeople(step, data)}
                  onOpen={() => openStep(step)}
                />
              ))}
            </SortableDataList>
            <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
              {steps.length} {plural(steps.length, 'bod', 'body', 'bodů')} ve scénáři
            </p>
          </Card>
        ) : (
          <EmptyState
            icon={<Route />}
            title="Váš obřad, vaše pořadí"
            description={
              canEdit
                ? 'Začněte jedním bodem. Vyberte si z návrhů pod „Přidat bod“ nebo si napište vlastní. Nic není předvybrané.'
                : 'Scénář zatím nemá žádné body.'
            }
          />
        )}
      </ScreenSection>
      {stepId && (step || (isNew && canEdit)) ? (
        <CeremonyStepSheet
          key={stepId}
          step={step}
          initialTitle={isNew ? initialTitle : ''}
          onClose={closeStep}
        />
      ) : stepId ? (
        <DetailSheet
          open
          title="Bod není dostupný"
          onOpenChange={(open) => {
            if (!open) closeStep()
          }}
        >
          <EmptyState
            placement="inline"
            icon={<Route />}
            title={isNew ? 'Máte přístup pouze ke čtení' : 'Tento bod už ve scénáři není'}
            description={
              isNew ? 'Nový bod může přidat správce svatby.' : 'Zavřete detail a vyberte jiný bod.'
            }
          />
        </DetailSheet>
      ) : null}
    </>
  )
}

function ScenarioRow({
  step,
  index,
  canEdit,
  people,
  onOpen,
}: {
  step: CeremonyStep
  index: number
  canEdit: boolean
  people: string[]
  onOpen: () => void
}) {
  return (
    <SortableDataListItem id={step.id} disabled={!canEdit}>
      <div className="group flex items-start gap-2 px-3 py-4 sm:gap-3 sm:px-5">
        {canEdit ? (
          <SortableDataListHandle
            label={`Přesunout ${step.title}`}
            className="size-9 opacity-50 group-hover:opacity-100 focus-visible:opacity-100"
          />
        ) : null}
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full border border-border bg-background text-xs font-semibold text-action tabular-nums">
          {index + 1}
        </span>
        <button
          type="button"
          id={`ceremony-step-${step.id}`}
          onClick={onOpen}
          aria-label={`Otevřít bod ${step.title}`}
          className="min-w-0 flex-1 rounded-control text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span className="block py-2 text-sm font-semibold break-words">{step.title}</span>
            {step.durationMinutes !== null ? (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                <Clock3 className="size-3.5" aria-hidden="true" />
                {step.durationMinutes} min
              </span>
            ) : null}
          </span>
          {step.description ? (
            <span className="mt-0.5 block break-words text-sm leading-relaxed text-muted-foreground">
              {step.description}
            </span>
          ) : null}
          {people.length ? (
            <span className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-muted-foreground">
              <Users className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>
                <span className="sr-only">Zapojení lidé: </span>
                {people.join(' · ')}
              </span>
            </span>
          ) : null}
        </button>
        {canEdit ? (
          <Button
            appearance="ghost"
            tone="neutral"
            size="icon"
            className="size-9"
            aria-label={`Upravit bod ${step.title}`}
            onClick={onOpen}
          >
            <PencilLine className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </SortableDataListItem>
  )
}
